// Production SharePoint Service using MSAL.js for secure browser authentication
import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser'
import { Client } from '@microsoft/microsoft-graph-client'
import { AuthenticationProvider, AuthenticationResult } from '@microsoft/microsoft-graph-client'

// SharePoint data interfaces
export interface SharePointClass {
  id?: string
  Title: string
  Instructor: string
  Description: string
  Date: string
  Time: string
  Duration: string
  Price: number
  Capacity: number
  Enrolled: number
  Level: string
  IsActive: boolean
  Materials?: string
  Prerequisites?: string
  Tags?: string
  ClassImage?: string
}

export interface SharePointProduct {
  id?: string
  Title: string
  Description: string
  Price: number
  Category: string
  Brand: string
  StockQuantity: number  // Maps to Kit.stockCount in local interface
  InStock: boolean
  IsActive: boolean
  SKU?: string
  Features?: string
  ProductImage?: string
}

export interface SharePointGalleryItem {
  id?: string
  Title: string
  Artist: string
  Description: string
  Price: number
  Category: string
  Medium: string
  Dimensions: string
  InStock: boolean
  IsActive: boolean
  IsShopifyProduct: boolean
  ShopifyId?: string
  Tags?: string
  ArtworkImage?: string
}

export interface SharePointCustomer {
  id?: string
  Title: string
  Email: string
  Phone?: string
  Address?: string
  EmailNotifications: boolean
  SMSNotifications: boolean
  PreferredClassTypes?: string
  RegistrationDate: string
  LastUpdated: string
  Notes?: string
  Paid: boolean
}

// MSAL Authentication Provider for Microsoft Graph
class MSALAuthenticationProvider implements AuthenticationProvider {
  private msalInstance: PublicClientApplication
  private scopes: string[]
  private tokenPromise: Promise<string> | null = null

  constructor(msalInstance: PublicClientApplication, scopes: string[]) {
    this.msalInstance = msalInstance
    this.scopes = scopes
  }

  async getAccessToken(): Promise<string> {
    // If a token request is already in progress, wait for it
    if (this.tokenPromise) {
      console.log('MSAL: Token request in progress, waiting...')
      return this.tokenPromise
    }

    // Start a new token request
    this.tokenPromise = this.doGetAccessToken()
    
    try {
      const token = await this.tokenPromise
      return token
    } finally {
      this.tokenPromise = null
    }
  }

  private async doGetAccessToken(): Promise<string> {
    console.log('MSAL: Getting access token...')
    const accounts = this.msalInstance.getAllAccounts()
    console.log(`MSAL: Found ${accounts.length} existing accounts`)
    
    if (accounts.length === 0) {
      console.log('MSAL: No accounts found, initiating login popup...')
      console.log('MSAL: Popup will open for Microsoft authentication')
      
      // Add timeout to login popup
      const loginPromise = this.msalInstance.loginPopup({
        scopes: this.scopes,
        prompt: 'select_account'
      })
      
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Login popup timeout after 60 seconds')), 60000)
      )
      
      const loginResponse = await Promise.race([loginPromise, timeoutPromise])
      console.log('MSAL: Login successful, access token received')
      return loginResponse.accessToken
    }

    console.log('MSAL: Found existing account, trying silent token acquisition...')
    // Try to get token silently first
    try {
      const silentRequest = {
        scopes: this.scopes,
        account: accounts[0]
      }
      
      const response = await this.msalInstance.acquireTokenSilent(silentRequest)
      console.log('MSAL: Silent token acquisition successful')
      return response.accessToken
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        console.log('MSAL: Silent acquisition failed, using popup...')
        console.log('MSAL: Opening popup for token refresh')
        
        // Add timeout to token popup
        const tokenPromise = this.msalInstance.acquireTokenPopup({
          scopes: this.scopes,
          account: accounts[0]
        })
        
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Token popup timeout after 60 seconds')), 60000)
        )
        
        const response = await Promise.race([tokenPromise, timeoutPromise])
        console.log('MSAL: Popup token acquisition successful')
        return response.accessToken
      }
      console.error('MSAL: Token acquisition failed:', error)
      throw error
    }
  }
}

class SharePointServiceProduction {
  private msalInstance: PublicClientApplication | null = null
  private graphClient: Client | null = null
  private siteId: string | null = null
  private config: any = {}
  private isInitializing: boolean = false
  private initializationPromise: Promise<void> | null = null

  constructor() {
    this.initializeConfig()
  }

  private initializeConfig() {
    // Safe environment variable access
    const getEnvVar = (key: string) => {
      try {
        const viteKey = key.replace('REACT_APP_', 'VITE_')
        return (import.meta.env && (import.meta.env[viteKey] || import.meta.env[key])) || 
               (typeof process !== 'undefined' && process.env && process.env[key]) || 
               ''
      } catch {
        return ''
      }
    }

    this.config = {
      tenantId: getEnvVar('REACT_APP_SHAREPOINT_TENANT_ID'),
      clientId: getEnvVar('REACT_APP_SHAREPOINT_CLIENT_ID'),
      siteUrl: getEnvVar('REACT_APP_SHAREPOINT_SITE_URL'),
      enabled: getEnvVar('REACT_APP_SHAREPOINT_ENABLED') === 'true'
    }
  }

  private async initializeMSAL(): Promise<void> {
    // If already initialized, return immediately
    if (this.msalInstance && this.graphClient) {
      console.log('SharePoint: MSAL already initialized')
      return
    }

    // If initialization is in progress, wait for it to complete
    if (this.isInitializing && this.initializationPromise) {
      console.log('SharePoint: MSAL initialization in progress, waiting...')
      return this.initializationPromise
    }

    // Start initialization
    this.isInitializing = true
    this.initializationPromise = this.doInitializeMSAL()

    try {
      await this.initializationPromise
    } finally {
      this.isInitializing = false
      this.initializationPromise = null
    }
  }

  private async doInitializeMSAL(): Promise<void> {
    console.log('SharePoint: Starting MSAL initialization...')
    console.log('SharePoint: Checking configuration...')
    if (!this.config.tenantId || !this.config.clientId) {
      throw new Error('SharePoint configuration missing. Please check TENANT_ID and CLIENT_ID in environment variables.')
    }

    console.log('SharePoint: Creating MSAL configuration...')
    const redirectUri = window.location.origin
    console.log('SharePoint: Using redirect URI:', redirectUri)
    
    // MSAL configuration
    const msalConfig = {
      auth: {
        clientId: this.config.clientId,
        authority: `https://login.microsoftonline.com/${this.config.tenantId}`,
        redirectUri
      },
      cache: {
        cacheLocation: 'localStorage' as const,
        storeAuthStateInCookie: false
      }
    }

    console.log('SharePoint: Initializing MSAL instance...')
    this.msalInstance = new PublicClientApplication(msalConfig)
    await this.msalInstance.initialize()

    console.log('SharePoint: Creating auth provider...')
    // Initialize Graph client with MSAL auth provider
    const authProvider = new MSALAuthenticationProvider(
      this.msalInstance,
      [
        'https://graph.microsoft.com/Sites.FullControl.All', 
        'https://graph.microsoft.com/Sites.Manage.All',
        'https://graph.microsoft.com/Files.ReadWrite.All',
        'https://graph.microsoft.com/User.Read'
      ]
    )

    console.log('SharePoint: Initializing Graph client...')
    try {
      this.graphClient = Client.initWithMiddleware({ authProvider })
      
      if (!this.graphClient) {
        throw new Error('Client.initWithMiddleware returned null')
      }
      
      console.log('SharePoint: MSAL initialization complete')
    } catch (error) {
      console.error('SharePoint: Graph client initialization failed:', error)
      throw new Error(`Graph client initialization failed: ${error.message}`)
    }
  }

  private async getSiteId(): Promise<string> {
    if (this.siteId) return this.siteId

    if (!this.config.siteUrl) {
      throw new Error('SharePoint site URL not configured')
    }

    // Extract hostname and site path from URL
    const url = new URL(this.config.siteUrl)
    const hostname = url.hostname
    const sitePath = url.pathname

    try {
      const site = await this.graphClient!.api(`/sites/${hostname}:${sitePath}`).get()
      this.siteId = site.id
      return this.siteId
    } catch (error) {
      console.error('Error getting site ID:', error)
      throw new Error(`Failed to get SharePoint site ID: ${error.message}`)
    }
  }

  async testConnection(): Promise<{ success: boolean; message: string }> {
    try {
      console.log('SharePoint: Starting connection test...')
      console.log('SharePoint config:', {
        tenantId: this.config.tenantId,
        clientId: this.config.clientId?.substring(0, 8) + '...',
        siteUrl: this.config.siteUrl,
        enabled: this.config.enabled
      })

      // Check if SharePoint is enabled
      if (!this.config.enabled) {
        return {
          success: false,
          message: '❌ SharePoint integration is disabled in configuration'
        }
      }

      // Check if required config exists
      if (!this.config.tenantId || !this.config.clientId || !this.config.siteUrl) {
        return {
          success: false,
          message: '❌ Missing required SharePoint configuration (tenant ID, client ID, or site URL)'
        }
      }

      console.log('SharePoint: Initializing MSAL...')
      await this.initializeMSAL()
      
      if (!this.graphClient) {
        throw new Error('Graph client failed to initialize')
      }

      console.log('SharePoint: Testing authentication with Microsoft Graph...')
      // Add timeout to Graph API calls
      const userPromise = this.graphClient.api('/me').get()
      const userTimeout = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('User info request timeout after 30 seconds')), 30000)
      )
      
      const userInfo = await Promise.race([userPromise, userTimeout])
      console.log(`SharePoint: Authentication successful - User: ${userInfo.displayName} (${userInfo.userPrincipalName})`)
      
      console.log('SharePoint: Getting site ID...')
      const siteId = await this.getSiteId()
      console.log(`SharePoint: Site ID: ${siteId}`)
      
      console.log('SharePoint: Testing site access...')
      const sitePromise = this.graphClient.api(`/sites/${siteId}`).get()
      const siteTimeout = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Site access request timeout after 30 seconds')), 30000)
      )
      
      const site = await Promise.race([sitePromise, siteTimeout])
      console.log(`SharePoint: Site access successful - ${site.displayName}`)
      
      return {
        success: true,
        message: `✅ Connected to SharePoint as ${userInfo.displayName}\n📂 Site: ${site.displayName}\n🔗 ${this.config.siteUrl}`
      }
    } catch (error) {
      console.error('SharePoint connection test failed:', error)
      
      // Provide specific guidance based on error type
      let errorMessage = `❌ Connection failed: ${error.message}`
      
      if (error.message && error.message.includes('redirect_uri_mismatch')) {
        errorMessage += `\n\n🔧 AZURE CONFIGURATION REQUIRED:\nThe Azure App Registration needs to include the current URL as a redirect URI.\n\n1. Go to Azure Portal > App Registrations\n2. Select app: ${this.config.clientId}\n3. Under Authentication > Single-page application\n4. Add redirect URI: ${window.location.origin}\n5. Save changes and retry`
      } else if (error.message && error.message.includes('AADSTS')) {
        errorMessage += `\n\n🔧 AZURE AUTHENTICATION ERROR:\nThis appears to be an Azure AD authentication issue. Please verify:\n1. App Registration permissions (Sites.ReadWrite.All, Files.ReadWrite.All, User.Read)\n2. Admin consent has been granted\n3. Redirect URI is configured correctly: ${window.location.origin}`
      }
      
      return {
        success: false,
        message: errorMessage
      }
    }
  }

  async createLists(): Promise<{ success: boolean; message: string }> {
    try {
      await this.initializeMSAL()
      const siteId = await this.getSiteId()

      const listsToCreate = [
        {
          displayName: 'SaltyMermaid_Classes',
          template: 'genericList',
          description: 'Art classes information and scheduling'
        },
        {
          displayName: 'SaltyMermaid_Kits',
          template: 'genericList',
          description: 'Art kits and supplies catalog'
        },
        {
          displayName: 'SaltyMermaid_Gallery',
          template: 'genericList',
          description: 'Artwork and artist information'
        },
        {
          displayName: 'SaltyMermaid_Customers',
          template: 'genericList',
          description: 'Customer registration and contact information'
        },
        {
          displayName: 'SaltyMermaid_KitOrders',
          template: 'genericList',
          description: 'Kit order tracking and fulfillment'
        }
      ]

      const results = []

      for (const listDef of listsToCreate) {
        try {
          // Check if list already exists
          const existingLists = await this.graphClient!.api(`/sites/${siteId}/lists`).filter(`displayName eq '${listDef.displayName}'`).get()
          
          if (existingLists.value.length > 0) {
            results.push(`✅ ${listDef.displayName} - Already exists`)
            // Update existing list with missing columns
            const columnResults = await this.addCustomColumns(siteId, existingLists.value[0].id, listDef.displayName)
            results.push(`🔄 ${listDef.displayName} - Column Updates:`)
            results.push(...columnResults.map(r => `  ${r}`))
          } else {
            // Create the list
            const createdList = await this.graphClient!.api(`/sites/${siteId}/lists`).post({
              displayName: listDef.displayName,
              template: listDef.template
            })
            results.push(`✅ ${listDef.displayName} - Created successfully`)
            
            // Add custom columns
            const columnResults = await this.addCustomColumns(siteId, createdList.id, listDef.displayName)
            results.push(`📋 ${listDef.displayName} - Column Creation:`)
            results.push(...columnResults.map(r => `  ${r}`))
          }
        } catch (error) {
          console.error(`Error with list ${listDef.displayName}:`, error)
          results.push(`❌ ${listDef.displayName} - Error: ${error.message}`)
        }
      }

      return {
        success: true,
        message: `SharePoint Lists Creation Results:\n\n${results.join('\n')}\n\n🎉 Lists are now available in your SharePoint site!`
      }
    } catch (error) {
      console.error('Error creating SharePoint lists:', error)
      return {
        success: false,
        message: `❌ Failed to create lists: ${error.message}`
      }
    }
  }

  private async addCustomColumns(siteId: string, listId: string, listName: string): Promise<string[]> {
    // Add essential columns for each list type
    const columnSets = {
      'SaltyMermaid_Classes': [
        { name: 'ClassDescription', text: { allowMultipleLines: true, appendChangesToExistingText: false, linesForEditing: 4 } },
        { name: 'Instructor', text: { allowMultipleLines: false, appendChangesToExistingText: false, linesForEditing: 1, maxLength: 255 } },
        { name: 'Date', dateTime: { displayAs: 'dateOnly', format: 'dateTime' } },
        { name: 'Time', text: { allowMultipleLines: false, appendChangesToExistingText: false, linesForEditing: 1, maxLength: 255 } },
        { name: 'Duration', text: { allowMultipleLines: false, appendChangesToExistingText: false, linesForEditing: 1, maxLength: 255 } },
        { name: 'ClassPrice', text: { allowMultipleLines: false, appendChangesToExistingText: false, linesForEditing: 1, maxLength: 255 } },
        { name: 'Capacity', text: { allowMultipleLines: false, appendChangesToExistingText: false, linesForEditing: 1, maxLength: 255 } },
        { name: 'Enrolled', text: { allowMultipleLines: false, appendChangesToExistingText: false, linesForEditing: 1, maxLength: 255 } },
        { name: 'Level', choice: { allowTextEntry: false, choices: ['Beginner', 'Intermediate', 'Advanced', 'All Levels'], displayAs: 'dropDownMenu' } },
        { name: 'IsActive', boolean: {} },
        { name: 'Materials', text: { allowMultipleLines: true, appendChangesToExistingText: false, linesForEditing: 6 } },
        { name: 'Prerequisites', text: { allowMultipleLines: true, appendChangesToExistingText: false, linesForEditing: 3 } },
        { name: 'Tags', text: { allowMultipleLines: true, appendChangesToExistingText: false, linesForEditing: 3 } },
        { name: 'ImageId', text: { allowMultipleLines: false, appendChangesToExistingText: false, linesForEditing: 1, maxLength: 255 } }
      ],
      'SaltyMermaid_Kits': [
        { name: 'KitDescription', text: { allowMultipleLines: true, appendChangesToExistingText: false, linesForEditing: 4 } },
        { name: 'KitPrice', text: { allowMultipleLines: false, appendChangesToExistingText: false, linesForEditing: 1, maxLength: 255 } },
        { name: 'Category', choice: { allowTextEntry: false, choices: ['Sea Glass Kits', 'Watercolor Kits', 'Resin Kits', 'Mixed Media Kits', 'Starter Kits'], displayAs: 'dropDownMenu' } },
        { name: 'Brand', text: { allowMultipleLines: false, appendChangesToExistingText: false, linesForEditing: 1, maxLength: 255 } },
        { name: 'StockQuantity', text: { allowMultipleLines: false, appendChangesToExistingText: false, linesForEditing: 1, maxLength: 255 } },
        { name: 'InStock', boolean: {} },
        { name: 'IsActive', boolean: {} },
        { name: 'SKU', text: { allowMultipleLines: false, appendChangesToExistingText: false, linesForEditing: 1, maxLength: 255 } },
        { name: 'Features', text: { allowMultipleLines: true, appendChangesToExistingText: false, linesForEditing: 6 } },
        { name: 'Tags', text: { allowMultipleLines: true, appendChangesToExistingText: false, linesForEditing: 3 } },
        { name: 'Weight', text: { allowMultipleLines: false, appendChangesToExistingText: false, linesForEditing: 1, maxLength: 255 } },
        { name: 'Dimensions', text: { allowMultipleLines: false, appendChangesToExistingText: false, linesForEditing: 1, maxLength: 255 } },
        { name: 'RelatedClassId', text: { allowMultipleLines: false, appendChangesToExistingText: false, linesForEditing: 1, maxLength: 255 } },
        { name: 'ImageId', text: { allowMultipleLines: false, appendChangesToExistingText: false, linesForEditing: 1, maxLength: 255 } }
      ],
      'SaltyMermaid_Gallery': [
        { name: 'ArtworkDescription', text: { allowMultipleLines: true, appendChangesToExistingText: false, linesForEditing: 4 } },
        { name: 'Artist', text: { allowMultipleLines: false, appendChangesToExistingText: false, linesForEditing: 1, maxLength: 255 } },
        { name: 'ArtworkPrice', text: { allowMultipleLines: false, appendChangesToExistingText: false, linesForEditing: 1, maxLength: 255 } },
        { name: 'Category', choice: { allowTextEntry: false, choices: ['painting', 'print', 'sculpture', 'mixed-media'], displayAs: 'dropDownMenu' } },
        { name: 'Medium', text: { allowMultipleLines: false, appendChangesToExistingText: false, linesForEditing: 1, maxLength: 255 } },
        { name: 'Dimensions', text: { allowMultipleLines: false, appendChangesToExistingText: false, linesForEditing: 1, maxLength: 255 } },
        { name: 'InStock', boolean: {} },
        { name: 'IsActive', boolean: {} },
        { name: 'IsShopifyProduct', boolean: {} },
        { name: 'ShopifyId', text: { allowMultipleLines: false, appendChangesToExistingText: false, linesForEditing: 1, maxLength: 255 } },
        { name: 'Tags', text: { allowMultipleLines: true, appendChangesToExistingText: false, linesForEditing: 3 } },
        { name: 'ImageId', text: { allowMultipleLines: false, appendChangesToExistingText: false, linesForEditing: 1, maxLength: 255 } }
      ],
      'SaltyMermaid_Customers': [
        { name: 'Email', text: { allowMultipleLines: false, appendChangesToExistingText: false, linesForEditing: 1, maxLength: 255 } },
        { name: 'Phone', text: { allowMultipleLines: false, appendChangesToExistingText: false, linesForEditing: 1, maxLength: 255 } },
        { name: 'Address', text: { allowMultipleLines: true, appendChangesToExistingText: false, linesForEditing: 4 } },
        { name: 'EmailNotifications', boolean: {} },
        { name: 'SMSNotifications', boolean: {} },
        { name: 'PreferredClassTypes', text: { allowMultipleLines: true, appendChangesToExistingText: false, linesForEditing: 4 } },
        { name: 'RegistrationDate', dateTime: { displayAs: 'dateOnly', format: 'dateTime' } },
        { name: 'LastUpdated', dateTime: { displayAs: 'dateTime', format: 'dateTime' } },
        { name: 'Notes', text: { allowMultipleLines: true, appendChangesToExistingText: false, linesForEditing: 6 } },
        { name: 'Paid', boolean: {} }
      ],
      'SaltyMermaid_KitOrders': [
        { name: 'OrderId', text: { allowMultipleLines: false, appendChangesToExistingText: false, linesForEditing: 1, maxLength: 255 } },
        { name: 'KitId', text: { allowMultipleLines: false, appendChangesToExistingText: false, linesForEditing: 1, maxLength: 255 } },
        { name: 'KitName', text: { allowMultipleLines: false, appendChangesToExistingText: false, linesForEditing: 1, maxLength: 255 } },
        { name: 'KitPrice', text: { allowMultipleLines: false, appendChangesToExistingText: false, linesForEditing: 1, maxLength: 255 } },
        { name: 'CustomerId', text: { allowMultipleLines: false, appendChangesToExistingText: false, linesForEditing: 1, maxLength: 255 } },
        { name: 'CustomerName', text: { allowMultipleLines: false, appendChangesToExistingText: false, linesForEditing: 1, maxLength: 255 } },
        { name: 'CustomerEmail', text: { allowMultipleLines: false, appendChangesToExistingText: false, linesForEditing: 1, maxLength: 255 } },
        { name: 'CustomerPhone', text: { allowMultipleLines: false, appendChangesToExistingText: false, linesForEditing: 1, maxLength: 255 } },
        { name: 'Quantity', text: { allowMultipleLines: false, appendChangesToExistingText: false, linesForEditing: 1, maxLength: 255 } },
        { name: 'TotalPrice', text: { allowMultipleLines: false, appendChangesToExistingText: false, linesForEditing: 1, maxLength: 255 } },
        { name: 'OrderDate', dateTime: { displayAs: 'dateTime', format: 'dateTime' } },
        { name: 'Status', choice: { allowTextEntry: false, choices: ['pending', 'paid', 'fulfilled', 'cancelled'], displayAs: 'dropDownMenu' } },
        { name: 'FulfillmentDate', dateTime: { displayAs: 'dateTime', format: 'dateTime' } },
        { name: 'CancellationDate', dateTime: { displayAs: 'dateTime', format: 'dateTime' } },
        { name: 'PaymentLinkId', text: { allowMultipleLines: false, appendChangesToExistingText: false, linesForEditing: 1, maxLength: 255 } },
        { name: 'PaymentLinkUrl', text: { allowMultipleLines: false, appendChangesToExistingText: false, linesForEditing: 1, maxLength: 500 } },
        { name: 'PaymentStatus', choice: { allowTextEntry: false, choices: ['unpaid', 'paid', 'refunded'], displayAs: 'dropDownMenu' } },
        { name: 'PaymentDate', dateTime: { displayAs: 'dateTime', format: 'dateTime' } },
        { name: 'PaymentMethod', text: { allowMultipleLines: false, appendChangesToExistingText: false, linesForEditing: 1, maxLength: 255 } },
        { name: 'Notes', text: { allowMultipleLines: true, appendChangesToExistingText: false, linesForEditing: 6 } },
        { name: 'InternalNotes', text: { allowMultipleLines: true, appendChangesToExistingText: false, linesForEditing: 6 } },
        { name: 'PreferredPickup', boolean: {} },
        { name: 'DeliveryAddress', text: { allowMultipleLines: true, appendChangesToExistingText: false, linesForEditing: 4 } },
        { name: 'FulfillmentMethod', choice: { allowTextEntry: false, choices: ['pickup', 'delivery'], displayAs: 'dropDownMenu' } },
        { name: 'TrackingNumber', text: { allowMultipleLines: false, appendChangesToExistingText: false, linesForEditing: 1, maxLength: 255 } },
        { name: 'LastUpdated', dateTime: { displayAs: 'dateTime', format: 'dateTime' } }
      ]
    }

    const columns = columnSets[listName] || []
    const results: string[] = []
    
    for (const columnDef of columns) {
      try {
        console.log(`Processing column ${columnDef.name} for list ${listName}`)
        
        // Check if column already exists
        const existingColumns = await this.graphClient!.api(`/sites/${siteId}/lists/${listId}/columns`).get()
        const columnExists = existingColumns.value.some((col: any) => col.name === columnDef.name || col.displayName === columnDef.name)
        
        if (columnExists) {
          console.log(`Column ${columnDef.name} already exists in ${listName}`)
          results.push(`✅ ${columnDef.name} - Already exists`)
          continue
        }

        // Create a copy without the 'name' property to avoid conflicts, then add it back properly
        const { name, ...columnDefWithoutName } = columnDef
        const columnData = {
          name: name,
          displayName: name,
          ...columnDefWithoutName
        }

        console.log(`Creating column ${columnDef.name} with data:`, JSON.stringify(columnData, null, 2))
        
        const result = await this.graphClient!.api(`/sites/${siteId}/lists/${listId}/columns`).post(columnData)
        console.log(`Successfully created column ${columnDef.name} in ${listName}:`, result)
        results.push(`✅ ${columnDef.name} - Created successfully`)
      } catch (error: any) {
        console.error(`Error creating column ${columnDef.name} in ${listName}:`, error)
        console.error('Error details:', {
          message: error.message,
          status: error.statusCode,
          body: error.body
        })
        results.push(`❌ ${columnDef.name} - Error: ${error.message}`)
      }
    }
    
    return results
  }

  async syncToSharePoint(): Promise<{ success: boolean; message: string }> {
    try {
      await this.initializeMSAL()
      const siteId = await this.getSiteId()

      // Get data from localStorage
      const classesData = JSON.parse(localStorage.getItem('salty_mermaid_classes') || '[]')
      const kitsData = JSON.parse(localStorage.getItem('salty_mermaid_kits') || '[]')
      const galleryData = JSON.parse(localStorage.getItem('salty_mermaid_gallery_items') || '[]')

      let syncResults = []

      // Sync classes
      if (classesData.length > 0) {
        const classesResult = await this.syncDataToList(siteId, 'SaltyMermaid_Classes', classesData, 'class')
        syncResults.push(`Classes: ${classesResult}`)
      }

      // Sync kits  
      if (kitsData.length > 0) {
        const kitsResult = await this.syncDataToList(siteId, 'SaltyMermaid_Kits', kitsData, 'kit')
        syncResults.push(`Kits: ${kitsResult}`)
      }

      // Sync gallery
      if (galleryData.length > 0) {
        const galleryResult = await this.syncDataToList(siteId, 'SaltyMermaid_Gallery', galleryData, 'gallery')
        syncResults.push(`Gallery: ${galleryResult}`)
      }

      return {
        success: true,
        message: `✅ Sync to SharePoint completed!\n\n${syncResults.join('\n')}\n\nData is now available in your SharePoint lists.`
      }
    } catch (error) {
      console.error('Error syncing to SharePoint:', error)
      return {
        success: false,
        message: `❌ Sync failed: ${error.message}`
      }
    }
  }

  private async syncDataToList(siteId: string, listName: string, data: any[], type: string): Promise<string> {
    try {
      // Get list ID
      const lists = await this.graphClient!.api(`/sites/${siteId}/lists`).filter(`displayName eq '${listName}'`).get()

      if (lists.value.length === 0) {
        return `❌ List ${listName} not found`
      }

      const listId = lists.value[0].id

      // SECURITY FIX: Use upsert pattern instead of delete-all-then-insert
      // Fetch existing items from SharePoint
      let existingItems: any[] = []
      try {
        const response = await this.graphClient!.api(`/sites/${siteId}/lists/${listId}/items`)
          .expand('fields')
          .get()
        existingItems = response.value || []
        console.log(`📦 Found ${existingItems.length} existing items in ${listName}`)
      } catch (error) {
        console.warn(`Could not fetch existing items from ${listName}:`, error)
        // Continue with insert-only mode if fetch fails
      }

      // Create a map of existing items by Title (primary identifier)
      const existingItemsMap = new Map()
      existingItems.forEach(item => {
        if (item.fields && item.fields.Title) {
          existingItemsMap.set(item.fields.Title, item)
        }
      })

      let insertCount = 0
      let updateCount = 0
      let errorCount = 0
      const errors: string[] = []

      // Process each item in the data array
      for (const item of data) {
        try {
          const spItem = this.convertToSharePointItem(item, type)
          const itemTitle = item.title || item.name

          console.log(`Syncing ${type} item:`, itemTitle, 'converted to:', spItem)

          // Check if item already exists in SharePoint
          const existingItem = existingItemsMap.get(spItem.Title)

          if (existingItem) {
            // UPDATE existing item
            try {
              await this.graphClient!.api(`/sites/${siteId}/lists/${listId}/items/${existingItem.id}`)
                .patch({ fields: spItem })
              updateCount++
              console.log(`✅ Updated existing item: ${itemTitle}`)
            } catch (updateError: any) {
              errorCount++
              const errorMsg = `Failed to update ${itemTitle}: ${updateError.message}`
              errors.push(errorMsg)
              console.error(`❌ ${errorMsg}`, updateError)
            }
          } else {
            // INSERT new item
            try {
              await this.graphClient!.api(`/sites/${siteId}/lists/${listId}/items`)
                .post({ fields: spItem })
              insertCount++
              console.log(`✅ Inserted new item: ${itemTitle}`)
            } catch (insertError: any) {
              errorCount++
              const errorMsg = `Failed to insert ${itemTitle}: ${insertError.message}`
              errors.push(errorMsg)
              console.error(`❌ ${errorMsg}`, insertError)

              // Provide helpful error messages
              if (insertError.message && (insertError.message.includes('not recognized') || insertError.message.includes('does not exist'))) {
                console.error(`⚠️  FIELD MISSING: ${insertError.message}`)
                console.error(`💡 Solution: The SharePoint list is missing required columns.`)
              } else if (insertError.message && insertError.message.includes('date')) {
                console.error(`⚠️  DATE FORMAT ERROR: ${insertError.message}`)
                console.error(`💡 Current date value: "${item.date}" - needs to be in format YYYY-MM-DD`)
              }
            }
          }
        } catch (error: any) {
          errorCount++
          const errorMsg = `Unexpected error syncing ${item.title || item.name}: ${error.message}`
          errors.push(errorMsg)
          console.error(`❌ ${errorMsg}`, error)
        }
      }

      // Summary message
      const summary = `✅ ${insertCount} inserted, ${updateCount} updated${errorCount > 0 ? `, ${errorCount} errors` : ''}`

      if (errorCount > 0) {
        console.error(`⚠️  Sync completed with errors:`, errors)
      }

      return summary
    } catch (error: any) {
      console.error(`❌ Critical error in syncDataToList:`, error)
      return `❌ Error: ${error.message}`
    }
  }

  private convertToSharePointItem(item: any, type: string): any {
    switch (type) {
      case 'class':
        return {
          Title: item.title,
          ClassDescription: item.description,
          Instructor: item.instructor,
          Date: item.date,
          Time: item.time,
          Duration: item.duration,
          ClassPrice: item.price ? item.price.toString() : '0',
          Capacity: item.capacity ? item.capacity.toString() : '0',
          Enrolled: item.enrolled ? item.enrolled.toString() : '0',
          Level: item.level,
          IsActive: item.isActive,
          Materials: Array.isArray(item.materials) ? item.materials.join('\n') : (item.materials || ''),
          Prerequisites: item.prerequisites || '',
          Tags: Array.isArray(item.tags) ? item.tags.join(', ') : (item.tags || ''),
          ImageId: item.imageId || ''
        }
      case 'kit':
        return {
          Title: item.name,
          KitDescription: item.description,
          KitPrice: item.price ? item.price.toString() : '0',
          Category: item.category,
          Brand: item.brand,
          StockQuantity: item.stockCount ? item.stockCount.toString() : '0',
          InStock: item.inStock,
          IsActive: item.isActive,
          SKU: item.sku || '',
          Features: Array.isArray(item.features) ? item.features.join('\n') : (item.features || ''),
          Tags: Array.isArray(item.tags) ? item.tags.join(', ') : (item.tags || ''),
          Weight: item.weight ? item.weight.toString() : '0',
          Dimensions: item.dimensions || '',
          RelatedClassId: item.relatedClassId || '',
          ImageId: item.imageId || ''
        }
      case 'gallery':
        return {
          Title: item.title,
          ArtworkDescription: item.description,
          Artist: item.artist,
          ArtworkPrice: item.price ? item.price.toString() : '0',
          Category: item.category,
          Medium: item.medium,
          Dimensions: item.dimensions,
          InStock: item.inStock,
          IsActive: item.isActive,
          IsShopifyProduct: item.isShopifyProduct,
          ShopifyId: item.shopifyId || '',
          Tags: Array.isArray(item.tags) ? item.tags.join(', ') : (item.tags || ''),
          ImageId: item.imageId || ''
        }
      case 'customer':
        return {
          Title: item.name,
          Email: item.email,
          Phone: item.phone || '',
          Address: item.address || '',
          EmailNotifications: item.preferences?.emailNotifications || false,
          SMSNotifications: item.preferences?.smsNotifications || false,
          PreferredClassTypes: Array.isArray(item.preferences?.preferredClassTypes)
            ? item.preferences.preferredClassTypes.join(', ')
            : (item.preferences?.preferredClassTypes || ''),
          RegistrationDate: item.registrationDate,
          LastUpdated: item.lastUpdated,
          Notes: item.preferences?.notes || '',
          Paid: item.paid || false
        }
      case 'kitOrder':
        return {
          Title: `Order ${item.id}`,
          OrderId: item.id,
          KitId: item.kitId,
          KitName: item.kitName,
          KitPrice: item.kitPrice ? item.kitPrice.toString() : '0',
          CustomerId: item.customerId || '',
          CustomerName: item.customerName,
          CustomerEmail: item.customerEmail,
          CustomerPhone: item.customerPhone,
          Quantity: item.quantity ? item.quantity.toString() : '0',
          TotalPrice: item.totalPrice ? item.totalPrice.toString() : '0',
          OrderDate: item.orderDate,
          Status: item.status,
          FulfillmentDate: item.fulfillmentDate || '',
          CancellationDate: item.cancellationDate || '',
          PaymentLinkId: item.paymentLinkId || '',
          PaymentLinkUrl: item.paymentLinkUrl || '',
          PaymentStatus: item.paymentStatus || 'unpaid',
          PaymentDate: item.paymentDate || '',
          PaymentMethod: item.paymentMethod || '',
          Notes: item.notes || '',
          InternalNotes: item.internalNotes || '',
          PreferredPickup: item.preferredPickup || false,
          DeliveryAddress: item.deliveryAddress || '',
          FulfillmentMethod: item.fulfillmentMethod || '',
          TrackingNumber: item.trackingNumber || '',
          LastUpdated: item.lastUpdated
        }
      default:
        return item
    }
  }

  async loadFromSharePoint(): Promise<{ success: boolean; message: string }> {
    try {
      await this.initializeMSAL()
      const siteId = await this.getSiteId()

      console.log('Loading data from SharePoint lists...')
      
      // Load classes from SharePoint
      const classesResponse = await this.graphClient
        .api(`/sites/${siteId}/lists/SaltyMermaid_Classes/items?$expand=fields`)
        .get()
      
      // Load products from SharePoint  
      const productsResponse = await this.graphClient
        .api(`/sites/${siteId}/lists/SaltyMermaid_Kits/items?$expand=fields`)
        .get()
        
      // Load gallery from SharePoint
      console.log('Loading gallery from SharePoint...')
      let galleryResponse
      try {
        galleryResponse = await this.graphClient
          .api(`/sites/${siteId}/lists/SaltyMermaid_Gallery/items?$expand=fields`)
          .get()
        console.log('Gallery response from SharePoint:', galleryResponse)
        console.log('Gallery response value length:', galleryResponse?.value?.length || 0)

        if (galleryResponse?.value?.length > 0) {
          console.log('Sample gallery item fields:', galleryResponse.value[0].fields)
        }
      } catch (galleryError) {
        console.error('Error loading gallery from SharePoint:', galleryError)
        console.log('Gallery list might not exist or be accessible. Creating empty gallery array.')
        galleryResponse = { value: [] }
      }

      // Convert SharePoint data to local format and save to localStorage
      const classes = classesResponse.value.map((item: any) => ({
        id: item.id.toString(),
        title: item.fields.Title || '',
        description: item.fields.ClassDescription || '',
        instructor: item.fields.Instructor || '',
        date: item.fields.Date || '',
        time: item.fields.Time || '',
        duration: item.fields.Duration || '',
        price: parseFloat(item.fields.ClassPrice) || 0,
        capacity: parseInt(item.fields.Capacity) || 0,
        enrolled: parseInt(item.fields.Enrolled) || 0,
        level: item.fields.Level || 'Beginner',
        isActive: item.fields.IsActive !== false,
        tags: item.fields.Tags ? item.fields.Tags.split(',') : [],
        materials: item.fields.Materials ? item.fields.Materials.split(',') : [],
        prerequisites: item.fields.Prerequisites || '',
        imageId: item.fields.ImageId || ''
      }))

      const kits = productsResponse.value.map((item: any) => ({
        id: item.id.toString(),
        name: item.fields.Title || '',
        description: item.fields.KitDescription || '',
        price: parseFloat(item.fields.KitPrice) || 0,
        category: item.fields.Category || '',
        brand: item.fields.Brand || '',
        stockCount: parseInt(item.fields.StockQuantity) || 0,
        inStock: item.fields.InStock !== false,
        isActive: item.fields.IsActive !== false,
        sku: item.fields.SKU || '',
        features: item.fields.Features ? item.fields.Features.split(',') : [],
        tags: item.fields.Tags ? item.fields.Tags.split(',') : [],
        weight: parseFloat(item.fields.Weight) || 0,
        dimensions: item.fields.Dimensions || '',
        relatedClassId: item.fields.RelatedClassId || '',
        imageId: item.fields.ImageId || ''
      }))

      console.log('Converting gallery response to local format...')
      const gallery = galleryResponse.value.map((item: any) => {
        console.log('Processing gallery item ID:', item.id)
        console.log('Gallery item fields:', JSON.stringify(item.fields, null, 2))

        // Check which fields are missing
        const expectedFields = ['Title', 'Artist', 'ArtworkDescription', 'ArtworkPrice', 'Category', 'Medium', 'Dimensions', 'InStock', 'IsActive', 'IsShopifyProduct', 'ShopifyId', 'Tags', 'ImageId']
        const missingFields = expectedFields.filter(field => !(field in item.fields))
        const presentFields = expectedFields.filter(field => field in item.fields)

        console.log('Missing gallery fields:', missingFields)
        console.log('Present gallery fields:', presentFields)

        const mappedItem = {
          id: item.id.toString(),
          title: item.fields.Title || '',
          artist: item.fields.Artist || '',
          description: item.fields.ArtworkDescription || '',
          price: parseFloat(item.fields.ArtworkPrice) || 0,
          category: item.fields.Category || '',
          medium: item.fields.Medium || '',
          dimensions: item.fields.Dimensions || '',
          inStock: item.fields.InStock !== false,
          isActive: item.fields.IsActive !== false,
          isShopifyProduct: item.fields.IsShopifyProduct !== false,
          shopifyId: item.fields.ShopifyId || '',
          tags: item.fields.Tags ? item.fields.Tags.split(',') : [],
          imageId: item.fields.ImageId || ''
        }

        console.log('Mapped gallery item:', JSON.stringify(mappedItem, null, 2))
        return mappedItem
      })
      console.log('Final gallery array length:', gallery.length)
      console.log('Gallery items after mapping:', gallery)

      // Save to localStorage
      localStorage.setItem('salty_mermaid_classes', JSON.stringify(classes))
      localStorage.setItem('salty_mermaid_kits', JSON.stringify(kits))
      localStorage.setItem('salty_mermaid_gallery_items', JSON.stringify(gallery))

      console.log('Saved to localStorage:')
      console.log('- Classes:', classes.length, 'items')
      console.log('- Kits:', kits.length, 'items')
      console.log('- Gallery:', gallery.length, 'items')

      // Verify localStorage save worked
      const savedGallery = localStorage.getItem('salty_mermaid_gallery_items')
      console.log('Verification - Gallery from localStorage:', savedGallery ? JSON.parse(savedGallery).length : 0, 'items')

      console.log(`Loaded ${classes.length} classes, ${kits.length} kits, ${gallery.length} gallery items`)
      
      return {
        success: true,
        message: `✅ Successfully loaded ${classes.length} classes, ${kits.length} kits, and ${gallery.length} gallery items from SharePoint`
      }
    } catch (error: any) {
      console.error('Load from SharePoint error:', error)
      return {
        success: false,
        message: `❌ Load failed: ${error.message}`
      }
    }
  }

  // Image library support methods
  async getGraphClient(): Promise<Client | null> {
    await this.initializeMSAL()
    return this.graphClient
  }

  async getSiteIdForImages(): Promise<string | null> {
    await this.initializeMSAL()
    return this.siteId
  }

  async createImageLibrary(): Promise<{ success: boolean; message: string }> {
    try {
      await this.initializeMSAL()
      const siteId = await this.getSiteId()

      // Check if library already exists
      try {
        const existingLibrary = await this.graphClient!
          .api(`/sites/${siteId}/lists`)
          .filter("displayName eq 'Website Images'")
          .get()

        if (existingLibrary.value && existingLibrary.value.length > 0) {
          return { success: true, message: 'Website Images document library already exists' }
        }
      } catch (error) {
        console.log('Library does not exist, creating new one')
      }

      // Create document library
      const libraryData = {
        displayName: 'Website Images',
        description: 'Image storage for website classes, kits, and gallery items',
        list: {
          template: 'documentLibrary'
        }
      }

      const newLibrary = await this.graphClient!
        .api(`/sites/${siteId}/lists`)
        .post(libraryData)

      // Add custom columns for categorization
      const categoryColumn = {
        displayName: 'Category',
        enforceUniqueValues: false,
        hidden: false,
        indexed: false,
        required: false,
        choice: {
          allowTextEntry: false,
          displayAs: 'dropDownMenu',
          choices: ['classes', 'kits', 'gallery']
        }
      }

      await this.graphClient!
        .api(`/sites/${siteId}/lists/${newLibrary.id}/columns`)
        .post(categoryColumn)

      const itemIdColumn = {
        displayName: 'ItemId',
        enforceUniqueValues: false,
        hidden: false,
        indexed: true,
        required: false,
        text: {
          allowMultipleLines: false,
          appendChangesToExistingText: false,
          linesForEditing: 1,
          maxLength: 255
        }
      }

      await this.graphClient!
        .api(`/sites/${siteId}/lists/${newLibrary.id}/columns`)
        .post(itemIdColumn)

      return { 
        success: true, 
        message: 'Website Images document library created successfully with custom columns' 
      }
    } catch (error: any) {
      console.error('Failed to create image library:', error)
      return { 
        success: false, 
        message: `Failed to create image library: ${error.message}` 
      }
    }
  }

  // Individual item sync methods
  async syncClassItem(classItem: any): Promise<{ success: boolean; message: string }> {
    try {
      await this.initializeMSAL()
      const siteId = await this.getSiteId()
      
      const result = await this.syncDataToList(siteId, 'SaltyMermaid_Classes', [classItem], 'class')
      return {
        success: true,
        message: `Successfully synced class: ${classItem.title}`
      }
    } catch (error: any) {
      console.error('Failed to sync class item:', error)
      return {
        success: false,
        message: `Failed to sync class: ${error.message}`
      }
    }
  }

  async syncKitItem(kitItem: any): Promise<{ success: boolean; message: string }> {
    try {
      await this.initializeMSAL()
      const siteId = await this.getSiteId()
      
      const result = await this.syncDataToList(siteId, 'SaltyMermaid_Kits', [kitItem], 'kit')
      return {
        success: true,
        message: `Successfully synced kit: ${kitItem.title}`
      }
    } catch (error: any) {
      console.error('Failed to sync kit item:', error)
      return {
        success: false,
        message: `Failed to sync kit: ${error.message}`
      }
    }
  }

  async syncGalleryItem(galleryItem: any): Promise<{ success: boolean; message: string }> {
    try {
      await this.initializeMSAL()
      const siteId = await this.getSiteId()
      
      const result = await this.syncDataToList(siteId, 'SaltyMermaid_Gallery', [galleryItem], 'gallery')
      return {
        success: true,
        message: `Successfully synced gallery item: ${galleryItem.title}`
      }
    } catch (error: any) {
      console.error('Failed to sync gallery item:', error)
      return {
        success: false,
        message: `Failed to sync gallery item: ${error.message}`
      }
    }
  }

  async deleteClassItem(itemId: string): Promise<{ success: boolean; message: string }> {
    try {
      await this.initializeMSAL()
      const siteId = await this.getSiteId()
      
      // Find and delete the item from SharePoint
      const listItems = await this.graphClient!
        .api(`/sites/${siteId}/lists/SaltyMermaid_Classes/items`)
        .expand('fields')
        .get()
      
      const itemToDelete = listItems.value.find((item: any) => item.fields.Title === itemId || item.fields.id === itemId)
      
      if (itemToDelete) {
        await this.graphClient!
          .api(`/sites/${siteId}/lists/SaltyMermaid_Classes/items/${itemToDelete.id}`)
          .delete()
        
        return {
          success: true,
          message: `Successfully deleted class from SharePoint`
        }
      }
      
      return {
        success: true,
        message: `Class not found in SharePoint (may have been already deleted)`
      }
    } catch (error: any) {
      console.error('Failed to delete class item:', error)
      return {
        success: false,
        message: `Failed to delete class: ${error.message}`
      }
    }
  }

  async deleteKitItem(itemId: string): Promise<{ success: boolean; message: string }> {
    try {
      await this.initializeMSAL()
      const siteId = await this.getSiteId()
      
      const listItems = await this.graphClient!
        .api(`/sites/${siteId}/lists/SaltyMermaid_Kits/items`)
        .expand('fields')
        .get()
      
      const itemToDelete = listItems.value.find((item: any) => item.fields.Title === itemId || item.fields.id === itemId)
      
      if (itemToDelete) {
        await this.graphClient!
          .api(`/sites/${siteId}/lists/SaltyMermaid_Kits/items/${itemToDelete.id}`)
          .delete()
        
        return {
          success: true,
          message: `Successfully deleted kit from SharePoint`
        }
      }
      
      return {
        success: true,
        message: `Kit not found in SharePoint (may have been already deleted)`
      }
    } catch (error: any) {
      console.error('Failed to delete kit item:', error)
      return {
        success: false,
        message: `Failed to delete kit: ${error.message}`
      }
    }
  }

  async deleteGalleryItem(itemId: string): Promise<{ success: boolean; message: string }> {
    try {
      await this.initializeMSAL()
      const siteId = await this.getSiteId()
      
      const listItems = await this.graphClient!
        .api(`/sites/${siteId}/lists/SaltyMermaid_Gallery/items`)
        .expand('fields')
        .get()
      
      const itemToDelete = listItems.value.find((item: any) => item.fields.Title === itemId || item.fields.id === itemId)
      
      if (itemToDelete) {
        await this.graphClient!
          .api(`/sites/${siteId}/lists/SaltyMermaid_Gallery/items/${itemToDelete.id}`)
          .delete()
        
        return {
          success: true,
          message: `Successfully deleted gallery item from SharePoint`
        }
      }
      
      return {
        success: true,
        message: `Gallery item not found in SharePoint (may have been already deleted)`
      }
    } catch (error: any) {
      console.error('Failed to delete gallery item:', error)
      return {
        success: false,
        message: `Failed to delete gallery item: ${error.message}`
      }
    }
  }

  // Customer management methods
  async syncCustomerItem(customerItem: any): Promise<{ success: boolean; message: string }> {
    try {
      await this.initializeMSAL()
      const siteId = await this.getSiteId()

      const result = await this.syncDataToList(siteId, 'SaltyMermaid_Customers', [customerItem], 'customer')
      return {
        success: true,
        message: `Successfully synced customer: ${customerItem.name}`
      }
    } catch (error: any) {
      console.error('Failed to sync customer item:', error)
      return {
        success: false,
        message: `Failed to sync customer: ${error.message}`
      }
    }
  }

  async loadCustomersFromSharePoint(): Promise<{ success: boolean; message: string; customers: any[] }> {
    try {
      await this.initializeMSAL()
      const siteId = await this.getSiteId()

      console.log('Loading customers from SharePoint...')

      // Load customers from SharePoint
      let customersResponse
      try {
        customersResponse = await this.graphClient
          .api(`/sites/${siteId}/lists/SaltyMermaid_Customers/items?$expand=fields`)
          .get()
        console.log('Customers response from SharePoint:', customersResponse)
      } catch (customerError) {
        console.error('Error loading customers from SharePoint:', customerError)
        console.log('Customer list might not exist or be accessible. Creating empty customers array.')
        customersResponse = { value: [] }
      }

      // Convert SharePoint data to local format
      const customers = customersResponse.value.map((item: any) => ({
        id: item.id.toString(),
        name: item.fields.Title || '',
        email: item.fields.Email || '',
        phone: item.fields.Phone || '',
        address: item.fields.Address || '',
        preferences: {
          emailNotifications: item.fields.EmailNotifications !== false,
          smsNotifications: item.fields.SMSNotifications === true,
          preferredClassTypes: item.fields.PreferredClassTypes
            ? item.fields.PreferredClassTypes.split(', ').filter((type: string) => type.trim())
            : [],
          notes: item.fields.Notes || ''
        },
        registrationDate: item.fields.RegistrationDate || new Date().toISOString(),
        lastUpdated: item.fields.LastUpdated || new Date().toISOString(),
        paid: item.fields.Paid === true
      }))

      console.log(`Loaded ${customers.length} customers from SharePoint`)

      return {
        success: true,
        message: `✅ Successfully loaded ${customers.length} customers from SharePoint`,
        customers
      }
    } catch (error: any) {
      console.error('Load customers from SharePoint error:', error)
      return {
        success: false,
        message: `❌ Load customers failed: ${error.message}`,
        customers: []
      }
    }
  }

  async deleteCustomerItem(customerId: string): Promise<{ success: boolean; message: string }> {
    try {
      await this.initializeMSAL()
      const siteId = await this.getSiteId()

      const listItems = await this.graphClient!
        .api(`/sites/${siteId}/lists/SaltyMermaid_Customers/items`)
        .expand('fields')
        .get()

      const itemToDelete = listItems.value.find((item: any) =>
        item.fields.Email === customerId || item.fields.id === customerId || item.id.toString() === customerId
      )

      if (itemToDelete) {
        await this.graphClient!
          .api(`/sites/${siteId}/lists/SaltyMermaid_Customers/items/${itemToDelete.id}`)
          .delete()

        return {
          success: true,
          message: `Successfully deleted customer from SharePoint`
        }
      }

      return {
        success: true,
        message: `Customer not found in SharePoint (may have been already deleted)`
      }
    } catch (error: any) {
      console.error('Failed to delete customer item:', error)
      return {
        success: false,
        message: `Failed to delete customer: ${error.message}`
      }
    }
  }

  // Kit Order management methods
  async syncKitOrderItem(orderItem: any): Promise<{ success: boolean; message: string }> {
    try {
      await this.initializeMSAL()
      const siteId = await this.getSiteId()

      const result = await this.syncDataToList(siteId, 'SaltyMermaid_KitOrders', [orderItem], 'kitOrder')
      return {
        success: true,
        message: `Successfully synced kit order: ${orderItem.id}`
      }
    } catch (error: any) {
      console.error('Sync kit order error:', error)
      return {
        success: false,
        message: `❌ Sync kit order failed: ${error.message}`
      }
    }
  }

  async loadKitOrdersFromSharePoint(): Promise<{ success: boolean; message: string; orders: any[] }> {
    try {
      await this.initializeMSAL()
      const siteId = await this.getSiteId()

      console.log('Loading kit orders from SharePoint...')

      const listItems = await this.graphClient!
        .api(`/sites/${siteId}/lists/SaltyMermaid_KitOrders/items`)
        .expand('fields')
        .get()

      const orders = listItems.value.map((item: any) => {
        const fields = item.fields
        return {
          id: fields.OrderId || fields.id,
          kitId: fields.KitId,
          kitName: fields.KitName,
          kitPrice: fields.KitPrice,
          customerId: fields.CustomerId,
          customerName: fields.CustomerName,
          customerEmail: fields.CustomerEmail,
          customerPhone: fields.CustomerPhone,
          quantity: fields.Quantity,
          totalPrice: fields.TotalPrice,
          orderDate: fields.OrderDate,
          status: fields.Status,
          fulfillmentDate: fields.FulfillmentDate,
          cancellationDate: fields.CancellationDate,
          notes: fields.Notes,
          preferredPickup: fields.PreferredPickup,
          deliveryAddress: fields.DeliveryAddress,
          lastUpdated: fields.LastUpdated
        }
      })

      console.log(`✅ Loaded ${orders.length} kit orders from SharePoint`)

      return {
        success: true,
        message: `✅ Loaded ${orders.length} kit orders from SharePoint`,
        orders
      }
    } catch (error: any) {
      console.error('Load kit orders from SharePoint error:', error)
      return {
        success: false,
        message: `❌ Load kit orders failed: ${error.message}`,
        orders: []
      }
    }
  }

  async deleteKitOrderItem(orderId: string): Promise<{ success: boolean; message: string }> {
    try {
      await this.initializeMSAL()
      const siteId = await this.getSiteId()

      const listItems = await this.graphClient!
        .api(`/sites/${siteId}/lists/SaltyMermaid_KitOrders/items`)
        .expand('fields')
        .get()

      const itemToDelete = listItems.value.find((item: any) =>
        item.fields.OrderId === orderId || item.fields.id === orderId || item.id.toString() === orderId
      )

      if (itemToDelete) {
        await this.graphClient!
          .api(`/sites/${siteId}/lists/SaltyMermaid_KitOrders/items/${itemToDelete.id}`)
          .delete()

        return {
          success: true,
          message: `Successfully deleted kit order from SharePoint`
        }
      }

      return {
        success: true,
        message: `Kit order not found in SharePoint (may have been already deleted)`
      }
    } catch (error: any) {
      console.error('Failed to delete kit order item:', error)
      return {
        success: false,
        message: `Failed to delete kit order: ${error.message}`
      }
    }
  }
}

// Export singleton instance
export const sharepointServiceProduction = new SharePointServiceProduction()